import { KeyStrategy } from './KeyStrategy';

type ItemWrapper<T> = {
  value: T;
};

export class PluggableStorage {
  constructor(private keyStrategy: KeyStrategy, private storage: Storage = localStorage) {}

  /**
   * Stores a value in the storage using the provided key strategy.
   */
  setItem<T>(key: string, value: T): void {
    const fullKey = this.keyStrategy.transformKey(key);
    const serialized = JSON.stringify({ value });
    this.storage.setItem(fullKey, serialized);
  }

  /**
   * Retrieves a value from the storage using the provided key strategy.
   * If the item does not exist, it returns the fallback value (default is null).
   */
  getItem<T, F>(key: string, fallback?: F): T | F {
    const fullKey = this.keyStrategy.transformKey(key);
    const raw = this.storage.getItem(fullKey);
    if (raw === null) {
      return fallback as F;
    }
    try {
      const data = JSON.parse(raw) as ItemWrapper<T>;
      return data?.value as T;
    } catch (err) {
      console.error(`PluggableStorage: error parsing item from storage for key "${fullKey}":`, err);
      return fallback as F;
    }
  }

  /**
   * Removes a key in the storage using the provided key strategy.
   */
  removeItem(key: string): void {
    const fullKey = this.keyStrategy.transformKey(key);
    this.storage.removeItem(fullKey);
  }

  /**
   * Clears all keys in the storage that match the key strategy.
   */
  clearMatching(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key && this.keyStrategy.matches(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((k) => this.storage.removeItem(k));
  }
}
